import React, { type ReactElement } from 'react';
import { faPersonRunning, faRobot } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import clsx from 'clsx';
import { markerDisplayName } from 'pages/marking/helpers/presentation.helpers';
import type { PartialOrganisationUserWithPartialUser } from 'services/assist-api/entities/customer-user/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import styles from './active-marker-badge.module.scss';

export type PartialTestInstanceResponseMarkerWithCustomerUser = (
  | { organisationUser: PartialOrganisationUserWithPartialUser }
  | PartialOrganisationUserWithPartialUser
) &
  Pick<TestInstanceResponseMarkerWithCustomerUser, 'id'>;

type MarkerDetails = {
  activeMarker: PartialTestInstanceResponseMarkerWithCustomerUser | undefined;
  defaultActiveMarkerToOrgUser?: boolean;
  organisationUser: PartialOrganisationUserWithPartialUser | undefined;
  isAiMarked: boolean | undefined;
};

type ActiveMarkerProps = { displayWarning: boolean } & MarkerDetails;

export const markedByAi = 'Marked by AI';
export const noActiveMarkerFound = 'No Active Marker Found';

function displayName(markerDetails: MarkerDetails): string {
  const { activeMarker, defaultActiveMarkerToOrgUser, organisationUser, isAiMarked } =
    markerDetails;
  if (isAiMarked) return markedByAi;
  if (activeMarker || defaultActiveMarkerToOrgUser) return markerDisplayName(organisationUser);
  return noActiveMarkerFound;
}

export default function ActiveMarkerBadge({
  displayWarning,
  activeMarker,
  defaultActiveMarkerToOrgUser,
  organisationUser,
  isAiMarked
}: ActiveMarkerProps): ReactElement {
  const markerDetails = {
    activeMarker,
    defaultActiveMarkerToOrgUser,
    organisationUser,
    isAiMarked
  };

  return (
    <span
      className={clsx(
        'badge rounded-pill',
        { 'text-bg-success': !displayWarning },
        { 'text-bg-warning': displayWarning },
        {
          'text-bg-danger':
            (!activeMarker && !defaultActiveMarkerToOrgUser) || !organisationUser?.id
        },
        { 'text-bg-info': isAiMarked }
      )}
    >
      <FontAwesomeIcon
        icon={isAiMarked ? faRobot : faPersonRunning}
        className={styles.activeMarkerIcon}
      />{' '}
      <span className={styles.activeMarker}>{displayName(markerDetails)}</span>
    </span>
  );
}
